/// <reference types="vite/client" />
// packages/web/src/lib/api.ts
// Typed API Client using ky with automatic JWT attachment & error handling

import ky from 'ky'
import type { ApiResponse, ApiError } from '@buddys-choice/shared'
import { AUTH_TOKEN_KEY, clearAuthStorage } from './authStorage'

const API_BASE = import.meta.env.VITE_API_URL || '/api'

export const api = ky.create({
  prefixUrl: API_BASE,
  timeout: 15_000,
  hooks: {
    beforeError: [
      async (error) => {
        if (error.name === 'TimeoutError') {
          error.message =
            `API not reachable at ${API_BASE}. Start backend: cd packages/api && pnpm dev`
          return error
        }

        // Surface the API's { error: { message, details } } body instead of
        // ky's generic "Request failed with status code ..." string.
        if (error.response) {
          try {
            const body = await error.response.clone().json() as ApiError
            if (body?.error?.message) {
              const fieldMessages = body.error.details
                ? Object.values(body.error.details).flat().join(' ')
                : ''
              error.message = fieldMessages
                ? `${body.error.message} ${fieldMessages}`
                : body.error.message
            }
          } catch {
            // Response wasn't JSON (e.g. HTML error page) — keep ky's default message
          }
        }
        return error
      },
    ],
    beforeRequest: [
      (request) => {
        const token = localStorage.getItem(AUTH_TOKEN_KEY)
        if (token) {
          request.headers.set('Authorization', `Bearer ${token}`)
        }
      },
    ],
    afterResponse: [
      async (_request, _options, response) => {
        if (response.status === 401 || response.status === 403) {
          // Token expired, invalid or forbidden — clear session
          clearAuthStorage()
          if (window.location.pathname.startsWith('/admin')) {
            window.location.href = `/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`
          } else if (window.location.pathname.startsWith('/account') || window.location.pathname === '/checkout') {
            window.location.href = `/account/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`
          }
        }
      },
    ],
  },
})

// Helper functions
export async function apiGet<T>(url: string, searchParams?: Record<string, string | number | boolean | undefined>): Promise<T> {
  const params = new URLSearchParams()
  if (searchParams) {
    Object.entries(searchParams).forEach(([k, v]) => {
      if (v !== undefined && v !== '') params.append(k, String(v))
    })
  }

  const res = await api.get(url, { searchParams: params }).json<ApiResponse<T>>()
  return res.data
}

export async function apiGetWithMeta<T>(url: string, searchParams?: Record<string, string | number | boolean | undefined>): Promise<ApiResponse<T>> {
  const params = new URLSearchParams()
  if (searchParams) {
    Object.entries(searchParams).forEach(([k, v]) => {
      if (v !== undefined && v !== '') params.append(k, String(v))
    })
  }

  return await api.get(url, { searchParams: params }).json<ApiResponse<T>>()
}

export async function apiPost<T, B = unknown>(url: string, body?: B): Promise<T> {
  const res = await api.post(url, { json: body }).json<ApiResponse<T>>()
  return res.data
}

export async function apiPut<T, B = unknown>(url: string, body?: B): Promise<T> {
  const res = await api.put(url, { json: body }).json<ApiResponse<T>>()
  return res.data
}

export async function apiPatch<T, B = unknown>(url: string, body?: B): Promise<T> {
  const res = await api.patch(url, { json: body }).json<ApiResponse<T>>()
  return res.data
}

export async function apiDelete<T>(url: string): Promise<T> {
  const res = await api.delete(url).json<ApiResponse<T>>()
  return res.data
}

export async function apiUpload<T>(url: string, formData: FormData): Promise<T> {
  const res = await api.post(url, { body: formData }).json<ApiResponse<T>>()
  return res.data
}
